import React, { useContext, useEffect, useState } from "react";
import { cloudinaryUrl } from "../constants/CloudinaryConstants";
import { HOST } from "../constants/HostConstants";
import { Link, useNavigate, useParams } from "react-router-dom";
import { propertyById } from "../actions/propertyById";
import { propertyImpressions } from "../actions/propertyImpression";
import { userByIdPreview } from "../actions/userById";
import Lender from "../components/Lender";
import VideoEmbed from "../components/VideoEmbed";
import Footer from "../components/Footer";
import { advertiseGrab } from "../actions/advertiseGrab";
import { SHOW_ADS } from "../constants/FeatureFlags";
import TitleCompany from "../components/TitleCompany";
import Poster from "../components/Poster";
import { getDisplayName } from "../utils/displayName";
import { featureRetrieve } from "../actions/featureRetrieve";
import { commentCreate } from "../actions/commentCreateClient";
import { UserContext } from "../context/MainContext";
import { commentGet } from "../actions/commentGetClient";
import { commentReact } from "../actions/commentReactClient";
import { advertiseProfile } from "../actions/advertiseProfile";
import { commentReport } from "../actions/commentReportClient";
import { commentDeleteSelf } from "../actions/commentDeleteSelf";
import { commentDeleteAdmin } from "../actions/commentDeleteAdmin";
import Agent from "../components/Agent";
import Realtor from "../components/Realtor";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Images from "../components/Images";
import ModalEditProperty from "../components/ModalEditProperty";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Lottie from "lottie-react";
import PropertyNotFound from "../assets/animations/PropertyNotFound.json";
import ProfilePromotionsDisplay from "../components/ProfilePromotionsDisplay";
import { getActiveProfilePromotions } from "../actions/getActiveProfilePromotions";
const PropertyView = () => {
  const { id: rawId } = useParams();
  const decodedId = rawId ? decodeURIComponent(rawId) : rawId;
  const validId =
    decodedId && /^\d+$/.test(String(decodedId))
      ? Number(decodedId)
      : decodedId;
  const { user } = useContext(UserContext);
  const nav = useNavigate();
  const [properties, setProperties] = useState(null);
  const [poster, setPoster] = useState(null);
  const [image, setImage] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [comments, setComments] = useState([]);
  const [advertisement, setAdvertisement] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [modal, setModal] = useState(false);
  const [comment, setComment] = useState("");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [alert, setAlert] = useState(null);

  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportingCommentId, setReportingCommentId] = useState(null);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [postingModalOpen, setPostingModalOpen] = useState(false);
  const [postingStatus, setPostingStatus] = useState("loading"); // loading, success, error
  const [postingMessage, setPostingMessage] = useState("");
  const [featurePage, setFeaturePage] = useState(0);
  const [profileCarouselIndex, setProfileCarouselIndex] = useState(0);
  const [videoCarouselIndex, setVideoCarouselIndex] = useState(0);
  // Showing request modal state
  const [showingModalOpen, setShowingModalOpen] = useState(false);
  const [showingDate, setShowingDate] = useState("");
  const [showingTimeStart, setShowingTimeStart] = useState("");
  const [showingTimeEnd, setShowingTimeEnd] = useState("");
  const [showingMessage, setShowingMessage] = useState("");
  const [showingSubmitting, setShowingSubmitting] = useState(false);
  const [showingSuccess, setShowingSuccess] = useState(false);
  // Admin delete modal state
  const [adminDeleteModalOpen, setAdminDeleteModalOpen] = useState(false);
  const [adminDeleteCommentId, setAdminDeleteCommentId] = useState(null);
  const [adminDeleteReason, setAdminDeleteReason] = useState("");
  const [adminDeleteSubmitting, setAdminDeleteSubmitting] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Check if current user is admin
  const isAdmin = user?.user_id === 3 || user?.user_id === 16;

  // Auto-scroll professional profiles carousel
  useEffect(() => {
    if (!profiles || profiles.length <= 2) return;
    const interval = setInterval(() => {
      setProfileCarouselIndex((prev) => {
        const maxIndex = Math.ceil(profiles.length / 2) - 1;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [profiles]);

  useEffect(() => {
    let mounted = true;
    const loadProperty = async () => {
      if (!validId) {
        setAlert("Invalid property id in URL.");
        if (process.env.NODE_ENV !== "production") {
        }
        setProperties(null);
        setNotFound(true);
        return;
      }
      try {
        const res = await propertyById({ setProperties }, validId);
        // propertyById returns null when not found / on error
        if (!res) setNotFound(true);
        else setNotFound(false);
      } catch (err) {
        setNotFound(true);
      }
    };
    loadProperty();
    return () => {
      mounted = false;
    };
  }, [validId]);

  useEffect(() => {
    if (properties) {
      // Ensure main image is set to the first media URL (or placeholder)
      if (properties?.media_url) {
        const first = properties.media_url.split(",")[0];
        setImage(
          cloudinaryUrl(first) ||
            "https://placehold.co/400x400?text=Property+Photo",
        );
      }
      userByIdPreview(properties?.user_id, { setPoster });
      if (SHOW_ADS) {
        advertiseGrab("horizontal", setAdvertisement);
      }
      if (properties?.feature_list) {
        featureRetrieve(properties?.feature_list, { setFeatures });
      }
      commentGet(properties?.property_id, setComments);
      advertiseProfile(setProfiles);
      // Fetch active profile promotions
      getActiveProfilePromotions(properties?.property_id).then((data) => {
        setPromotions(Array.isArray(data) ? data : []);
      });
    }
  }, [properties]);

  // Track property impression ONCE per page view
  useEffect(() => {
    if (validId) {
      propertyImpressions(validId).finally(() => {
        propertyById({ setProperties }, validId);
      });
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Placeholder image for missing/loading property photos
  const propertyPhotoPlaceholder =
    "https://placehold.co/400x400?text=Property+Photo";

  function currencyFormat(num) {
    if (!num) {
      return "";
    }
    let money = "$" + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    return money.substring(0, money.length - 3);
  }

  function toNumber(value) {
    if (value === null || value === undefined || value === "") return NaN;
    if (typeof value === "number") return value;
    const cleaned = String(value).replace(/[^0-9.-]+/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  // Helper: check feature by id or by direct property column aliases
  const hasFeature = (id, aliases = []) => {
    if (!properties) return false;
    const fl = (properties.feature_list || "")
      .split(",")
      .map((s) => (s || "").trim())
      .filter(Boolean);
    if (fl.includes(id)) return true;
    const direct = properties[id];
    if (
      direct === "Yes" ||
      direct === "yes" ||
      direct === true ||
      direct === 1 ||
      direct === "1"
    )
      return true;
    for (const a of aliases) {
      const v = properties[a];
      if (!v) continue;
      if (v === "Yes" || v === "yes" || v === true || v === 1 || v === "1")
        return true;
    }
    return false;
  };

  // Check any pet-related data exists on the property row
  const hasAnyPetData = () => {
    if (!properties) return false;
    if (properties.pet_deposit) return true;
    const petIds = ["465", "471", "472", "473", "474", "475", "476"];
    for (const id of petIds) if (hasFeature(id)) return true;
    // also check common column names
    const keys = Object.keys(properties || {});
    for (const k of keys) {
      if (/pet|pets|dog|cat|fish|bird/i.test(k)) {
        const v = properties[k];
        if (v === "Yes" || v === "yes" || v === true || v === 1 || v === "1")
          return true;
        if (typeof v === "string" && v.trim() !== "") return true; // non-empty string may indicate details
      }
    }
    return false;
  };

  // Detect a specific pet type by id, direct numeric column, or any matching property key
  const typeHas = (id, nameRegex) => {
    if (hasFeature(id)) return true;
    if (!properties) return false;
    // check columns with names matching regex
    const keys = Object.keys(properties || {});
    for (const k of keys) {
      if (nameRegex.test(k)) {
        const v = properties[k];
        if (v === "Yes" || v === "yes" || v === true || v === 1 || v === "1")
          return true;
      }
    }
    return false;
  };

  // Handle showing request submission
  const handleShowingSubmit = async () => {
    if (!showingDate || !showingTimeStart || !showingTimeEnd) {
      alert("Please select a date and time range for your showing.");
      return;
    }

    setShowingSubmitting(true);
    try {
      const res = await fetch(`${HOST}/api/property/sendShowingRequest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          agentEmail: poster?.email_address,
          agentName: `${poster?.legal_first_name || ""} ${
            poster?.legal_last_name || ""
          }`.trim(),
          propertyAddress: `${properties?.street_number || ""} ${
            properties?.street_name || ""
          }, ${properties?.city || ""}, TX ${properties?.zip || ""}`,
          propertyId: properties?.property_id,
          propertyUrl: `${window.location.origin}/property/${properties?.property_id}`,
          requesterName:
            `${user?.legal_first_name || ""} ${
              user?.legal_last_name || ""
            }`.trim() || "Interested Buyer",
          requesterEmail: user?.email_address || "",
          requesterPhone: user?.phone_number || "",
          showingDate,
          showingTimeStart,
          showingTimeEnd,
          message: showingMessage,
        }),
      });

      if (res.ok) {
        setShowingSuccess(true);
      } else {
        const err = await res.text();
        alert(`Failed to send showing request: ${err}`);
      }
    } catch (error) {
      alert("Failed to send showing request. Please try again.");
    } finally {
      setShowingSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20">
        <div style={{ width: 600, height: 600 }}>
          <Lottie animationData={PropertyNotFound} loop={true} />
        </div>
        <h2 className="mt-4 text-2xl font-bold">
          Oops — that property was not found
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          It may have been removed or the link is incorrect.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1600px] mx-auto mt-8 h-screen">
      {alert && (
        <div className="w-full text-center bg-red-50 border border-red-200 text-red-800 p-3 rounded">
          {alert}
        </div>
      )}

      <div className="flex flex-col tablet:flex-row gap-6 w-full">
        <div className="flex-[0.5]  flex flex-col gap-6 ">
          <div className="flex justify-between items-center ">
            <div className="flex flex-wrap items-center gap-2">
              {/* NEW LISTING badge (shows for 1 week, not Sold/Leased/Withdrawn/Expired) */}
              {properties?.listing_start_date &&
                (() => {
                  const listingDate = new Date(properties.listing_start_date);
                  const now = new Date();
                  const diffTime = now - listingDate;
                  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                  return (
                    diffDays <= 7 &&
                    !["Sold", "Leased", "Withdrawn", "Expired"].includes(
                      properties?.property_status,
                    )
                  );
                })() && (
                  <span className="font-bold uppercase bg-gradient-to-r from-pink-400 to-pink-600 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base shadow-lg">
                    NEW LISTING
                  </span>
                )}

              {/* Status badge: show current property_status for all statuses except 'Active' */}
              {(() => {
                const st = properties?.property_status;
                if (!st || typeof st !== "string") return null;
                const clean = st.trim();
                // Only suppress the badge when status is Active
                if (!clean || clean === "Active") return null;
                return (
                  <span className="font-bold uppercase bg-gray-700 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base">
                    {clean}
                  </span>
                );
              })()}

              {/* FOR LEASE badge (shows if for === Lease and not Leased) */}
              {properties?.for === "Lease" &&
                properties?.property_status !== "Leased" && (
                  <span className="font-bold uppercase bg-teal-500 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base">
                    FOR LEASE
                  </span>
                )}

              {/* LEASED badge (shows if for === Lease and property_status === Leased) */}
              {properties?.for === "Lease" &&
                properties?.property_status === "Leased" && (
                  <span className="font-bold uppercase bg-red-500 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base">
                    LEASED
                  </span>
                )}

              {/* FOR SALE badge (shows if for === Sale and not Sold) */}
              {properties?.for === "Sale" &&
                properties?.property_status !== "Sold" && (
                  <span className="font-bold uppercase bg-green-500 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base">
                    FOR SALE
                  </span>
                )}

              {/* SOLD badge (shows if for === Sale and property_status === Sold) */}
              {properties?.for === "Sale" &&
                properties?.property_status === "Sold" && (
                  <span className="font-bold uppercase bg-red-500 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base">
                    SOLD
                  </span>
                )}

              {/* TEMP OFF MARKET badge (accept legacy 'Not Active' and explicit 'Temporarily Off Market') */}
              {(properties?.property_status === "Not Active" ||
                properties?.property_status === "Temporarily Off Market") && (
                <span className="font-bold uppercase bg-yellow-500 text-white rounded-lg px-2 md:px-3 py-1 md:py-1.5 text-sm md:text-base">
                  TEMPORARILY OFF MARKET
                </span>
              )}

              {/* Property Type badge (Residential/Commercial) */}
              {properties?.type && (
                <span
                  className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-sm md:text-base font-semibold ${
                    properties.type === "Residential"
                      ? "bg-blue-100 text-blue-800"
                      : properties.type === "Commercial"
                        ? "bg-purple-100 text-purple-800"
                        : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {properties.type}
                </span>
              )}
              {/* Residential Subtype badge (e.g., Single Family) - shown next to type badge */}
              {properties?.for === "Lease" &&
                properties?.type === "Residential" &&
                hasAnyPetData() && (
                  <span className="px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-sm md:text-base font-semibold bg-gray-100 text-gray-800">
                    {properties.residentialSubtype}
                  </span>
                )}
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center text-30 font-semibold gap-1">
                <span role="img" aria-label="key">
                  🔑
                </span>
                {properties?.property_id}
              </span>
              {user && properties && user.user_id === properties.user_id && (
                <button
                  onClick={() => setModal(true)}
                  className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-all text-sm"
                >
                  Edit Property
                </button>
              )}
              {isAdmin && user?.user_id !== properties?.user_id && (
                <button
                  onClick={() => setModal(true)}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-all text-sm"
                >
                  Edit
                </button>
              )}
              {poster && (
                <button
                  onClick={() => {
                    setShowingModalOpen(true);
                    setShowingDate("");
                    setShowingTimeStart("");
                    setShowingTimeEnd("");
                    setShowingMessage("");
                    setShowingSuccess(false);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-all text-sm shadow-md hover:shadow-lg"
                >
                  📅 Schedule a Showing
                </button>
              )}
            </div>
          </div>

          <div className="mt-2 flex flex-col md:flex-row md:items-baseline md:gap-4">
            <span className="text-2xl font-bold">
              {`${properties?.street_number || ""} ${
                properties?.street_name || ""
              }${properties?.city ? ", " + properties.city : ""}${
                properties?.zip ? ", " + properties.zip : ""
              }`.trim() || ""}
            </span>
            {properties?.unit_number && (
              <span className="text-sm text-gray-500 ml-2 md:ml-0">
                Unit# {properties.unit_number}
              </span>
            )}

            {/* Price inline with address */}
            <span className="md:ml-4 mt-2 md:mt-0 text-lg font-semibold">
              {properties?.property_status === "Sold" ||
              properties?.property_status === "Leased"
                ? ""
                : properties?.price
                  ? currencyFormat(properties.price) +
                    (properties?.for === "Lease" ? " / Month" : "")
                  : ""}
            </span>

            {/* Financing options shown under address for Sale (Residential or Commercial) */}
            {properties?.financing_options &&
              properties?.for === "Sale" &&
              (properties?.type === "Commercial" ||
                properties?.type === "Residential") && (
                <div className="mt-2 md:mt-0 md:ml-4">
                  <span className="text-sm font-medium text-gray-700">
                    Financing Options:
                  </span>
                  <div className="gap-1 mt-1">
                    {properties.financing_options
                      .split(",")
                      .map((opt) => opt.trim())
                      .filter(Boolean)
                      .map((opt) => (
                        <span
                          key={opt}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {opt}
                        </span>
                      ))}
                  </div>
                </div>
              )}

            {properties?.for === "Lease" &&
              (properties?.type === "Commercial" ||
                properties?.type === "Residential") && (
                <span className="md:ml-2 mt-2 md:mt-0 text-sm text-gray-700">
                  {properties?.security_deposit ? (
                    <>
                      <span className="text-gray-400">|</span>
                      <span className="ml-2">
                        Security Deposit:{" "}
                        {currencyFormat(Number(properties.security_deposit))}
                      </span>
                    </>
                  ) : null}
                </span>
              )}
          </div>

          <div className="border-2 h-full p-4 ">
            {/* Top Horizontal Ad Slot */}
            <a href="/store" className="block w-full">
              <div className="w-full h-[150px] bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all cursor-pointer">
                <div className="text-center px-4">
                  <p className="font-semibold text-gray-500 text-sm mb-1">
                    ADVERTISEMENT
                  </p>
                  <p className="text-xs text-gray-400">793px x 150px</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click to inquire about ad placement
                  </p>
                </div>
              </div>
            </a>

            {/* Faded divider line */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-4"></div>

            <div className="flex flex-col gap-6 justify-center mt-6">
              {/* Faded divider line */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>

              <h1 className="font-bold leading-loose text-center text-xl">
                About This Property
              </h1>

              <div className="flex flex-col gap-6 justify-center ">
                {properties?.vacant_land === "Yes" ? (
                  <>
                    <div className="flex gap-2 justify-evenly flex-wrap">
                      <div className="flex flex-col items-center gap-2">
                        <h1 className="font-semibold text-xl">
                          {properties?.acreage || "N/A"}
                        </h1>
                        <p className="text-gray-600">Acreage</p>
                      </div>
                      <div className="flex flex-col items-center gap-2">
                        <h1 className="font-semibold text-xl">
                          {properties?.property_tax_number || "N/A"}
                        </h1>
                        <p className="text-gray-600">Property Tax ID #</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <h1 className="font-bold text-lg">Legal Description</h1>
                      <p className="text-gray-700 mt-2">
                        {properties?.legal_description || "Not provided"}
                      </p>
                    </div>
                    {/* Days On Market removed - now shown in the Posted on header */}
                  </>
                ) : (
                  <>
                    {/* Row 1: Beds and Baths */}
                    <div className="flex gap-6 justify-center flex-wrap">
                      <div className="flex flex-col items-center">
                        <h1 className="font-bold text-2xl">
                          {properties?.beds_total || "0"}
                        </h1>
                        <p className="text-gray-600 text-sm">Beds</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <h1 className="font-bold text-2xl">
                          {properties?.baths_full || "0"}
                        </h1>
                        <p className="text-gray-600 text-sm">Baths</p>
                      </div>
                    </div>

                    {/* Row 2: Living SF and Price/SF */}
                    <div className="flex gap-6 justify-center flex-wrap">
                      <div className="flex flex-col items-center">
                        <h1 className="font-bold text-2xl">
                          {properties?.living_sqft || "N/A"}
                        </h1>
                        <p className="text-gray-600 text-sm">Living SF</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <h1 className="font-bold text-2xl">
                          {properties?.price && properties?.living_sqft
                            ? (() => {
                                const priceNum = toNumber(properties.price);
                                const sqftNum = toNumber(
                                  properties.living_sqft,
                                );
                                if (
                                  !Number.isFinite(priceNum) ||
                                  !Number.isFinite(sqftNum) ||
                                  sqftNum === 0
                                ) {
                                  return "N/A";
                                }
                                // For leases, price is already per month so use as-is
                                const priceForCalc = priceNum;
                                return `$${(priceForCalc / sqftNum).toFixed(
                                  2,
                                )}`;
                              })()
                            : "N/A"}
                        </h1>
                        <p className="text-gray-600 text-sm">
                          Price / Living SF
                        </p>
                      </div>
                    </div>
                    {/* Row 3: Acreage */}
                    <div className="flex gap-6 justify-center flex-wrap">
                      <div className="flex flex-col items-center">
                        <h1 className="font-bold text-2xl">
                          {properties?.acreage || ".15"}
                        </h1>
                        <p className="text-gray-600 text-sm">Acreage</p>
                      </div>
                    </div>
                    {/* School info for residential Sale/Lease - moved under Acreage */}
                    {properties?.type === "Residential" &&
                      (properties?.for === "Sale" ||
                        properties?.for === "Lease") && (
                        <div className="flex flex-col items-center mt-4">
                          <h2 className="font-semibold">School District</h2>
                          <p className="text-gray-700">
                            {properties?.school_district || "Not specified"}
                          </p>
                          <div className="flex gap-6 mt-2 flex-wrap justify-center">
                            <div className="flex flex-col items-center">
                              <span className="text-sm text-gray-600">
                                Elementary
                              </span>
                              <span className="font-medium">
                                {properties?.elementary_school || "N/A"}
                              </span>
                            </div>
                            <div className="flex flex-col items-center">
                              <span className="text-sm text-gray-600">
                                Middle
                              </span>
                              <span className="font-medium">
                                {properties?.middle_school || "N/A"}
                              </span>
                            </div>
                            <div className="flex flex-col items-center">
                              <span className="text-sm text-gray-600">
                                High
                              </span>
                              <span className="font-medium">
                                {properties?.high_school || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    {properties?.legal_description && (
                      <>
                        <h1 className="text-base font-bold">
                          Legal Description
                        </h1>
                        <p className="relative -top-4 text-gray-700">
                          {properties?.legal_description || ""}
                        </p>
                      </>
                    )}
                    {/* Residence type removed per UI request */}
                    {/* Days On Market removed - now shown in the Posted on header */}
                  </>
                )}
              </div>
            </div>

            {/* Faded divider line */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-6"></div>

            <Poster poster={properties} />

            {/* Bottom Horizontal Ad Slot */}
            <a href="/store" className="block w-full my-6">
              <div className="w-full h-[150px] bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all cursor-pointer">
                <div className="text-center px-4">
                  <p className="font-semibold text-gray-500 text-sm mb-1">
                    ADVERTISEMENT
                  </p>
                  <p className="text-xs text-gray-400">793px x 150px</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click to inquire about ad placement
                  </p>
                </div>
              </div>
            </a>

            <Lender lender={properties} />
            <TitleCompany title={properties} />
            <Realtor realtor={properties} />

            {/* Featured Videos Carousel */}
            {(() => {
              const allVideos = [
                { url: properties?.embeded_url, number: 1 },
                { url: properties?.embeded_url2, number: 2 },
                { url: properties?.embeded_url3, number: 3 },
                { url: properties?.embeded_url4, number: 4 },
              ].filter((v) => v.url);

              if (allVideos.length === 0) return null;

              return (
                <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-6 space-y-4 mt-8">
                  <h2 className="text-lg font-bold text-slate-900">
                    Featured Videos ({allVideos.length})
                  </h2>
                  <div className="relative">
                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                      <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">
                        Video {allVideos[videoCarouselIndex]?.number}
                      </p>
                      <div className="flex justify-center">
                        <VideoEmbed
                          url={allVideos[videoCarouselIndex]?.url?.trim()}
                        />
                      </div>
                    </div>

                    {allVideos.length > 1 && (
                      <div className="flex items-center justify-between mt-4 gap-2">
                        <button
                          onClick={() =>
                            setVideoCarouselIndex(
                              videoCarouselIndex === 0
                                ? allVideos.length - 1
                                : videoCarouselIndex - 1,
                            )
                          }
                          className="p-2 bg-slate-200 hover:bg-slate-300 rounded-full transition-colors"
                          aria-label="Previous video"
                        >
                          <MdChevronLeft size={24} />
                        </button>

                        <div className="flex gap-2 justify-center flex-1">
                          {allVideos.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setVideoCarouselIndex(idx)}
                              className={`h-2 rounded-full transition-all ${
                                idx === videoCarouselIndex
                                  ? "bg-slate-900 w-6"
                                  : "bg-slate-300 w-2 hover:bg-slate-400"
                              }`}
                              aria-label={`Go to video ${idx + 1}`}
                            />
                          ))}
                        </div>

                        <button
                          onClick={() =>
                            setVideoCarouselIndex(
                              videoCarouselIndex === allVideos.length - 1
                                ? 0
                                : videoCarouselIndex + 1,
                            )
                          }
                          className="p-2 bg-slate-200 hover:bg-slate-300 rounded-full transition-colors"
                          aria-label="Next video"
                        >
                          <MdChevronRight size={24} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <p className="text-center mt-8">
              This property has {properties?.impressions} impressions.
            </p>
          </div>

          {/* Professional Profiles Carousel */}
          {profiles && profiles.length > 0 && (
            <div className="border-2 p-4">
              <h2 className="text-lg font-semibold text-slate-700 mb-3">
                Need some help from a professional?
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Contact one of our preferred professionals today!
              </p>
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{
                    transform: `translateX(-${profileCarouselIndex * 100}%)`,
                  }}
                >
                  {Array.from({ length: Math.ceil(profiles.length / 2) }).map(
                    (_, pageIndex) => (
                      <div key={pageIndex} className="min-w-full flex gap-4">
                        {profiles
                          .slice(pageIndex * 2, pageIndex * 2 + 2)
                          .map((profile) => (
                            <a
                              key={profile.user_id}
                              href={`/user/${profile.user_id}`}
                              className="flex-1 flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow"
                            >
                              <img
                                src={
                                  profile.picture_url
                                    ? cloudinaryUrl(profile.picture_url)
                                    : "/error.png"
                                }
                                alt={getDisplayName(profile) || "Professional"}
                                className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-900 truncate">
                                  {getDisplayName(profile)}
                                </h3>
                                <p className="text-xs text-slate-600 truncate">
                                  {profile.class_type || "Professional"}
                                </p>
                              </div>
                              <span className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap">
                                View
                              </span>
                            </a>
                          ))}
                      </div>
                    ),
                  )}
                </div>
              </div>
              {/* Carousel dots */}
              {profiles.length > 2 && (
                <div className="flex justify-center gap-2 mt-3">
                  {Array.from({ length: Math.ceil(profiles.length / 2) }).map(
                    (_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setProfileCarouselIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === profileCarouselIndex
                            ? "bg-blue-600"
                            : "bg-slate-300 hover:bg-slate-400"
                        }`}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-[0.5] flex-col  gap-6">
          <div className="flex flex-col gap-6">
            <div>
              {properties?.media_url?.length > 0 ? (
                <img
                  className="w-[100%] bg-slate-300 mx-auto h-[100%]"
                  src={image || propertyPhotoPlaceholder}
                  alt="property"
                />
              ) : (
                <img
                  className="w-[100%] bg-slate-300 mx-auto h-[100%]"
                  src={propertyPhotoPlaceholder}
                  alt="property"
                />
              )}
            </div>
            <div className="flex flex-wrap gap-6 justify-center">
              {properties?.media_url?.length > 0 ? (
                <Images
                  urls={properties.media_url.split(",")}
                  setImage={setImage}
                />
              ) : (
                ""
              )}
            </div>
          </div>

          {/* Public Agent Remarks - Visible to all users */}
          <div className="p-4 flex flex-col gap-6">
            <div className="flex flex-col">
              <h1 className="font-bold text-30">Listing Notes</h1>
              <p className="text-19">
                {properties?.agentremarks || "No notes provided"}
              </p>
            </div>

            {/* Private Agent Remarks - Visible only to real estate professionals */}
            {/* 
              REAL ESTATE PROFESSIONAL TIERS (can view private agent notes):
              - Realtor: Sales Agent (non-NAR), Realtor (NAR member), or Broker (company owner)
              - Builder: Builder Sales Agent
              - Title Company: Title Company Closer
              - Lender: Licensed Lender
              See REAL_ESTATE_CLASSIFICATIONS.md for detailed tier definitions
            */}
            {(user?.class_type === "Realtor" ||
              user?.class_type === "Builder" ||
              user?.class_type === "Title Company" ||
              user?.class_type === "Lender") && (
              <div className="flex flex-col border-t-2 pt-4 mt-4">
                <h1 className="font-bold text-30">Private Agent Notes</h1>
                <p className="text-19 text-blue-600 italic">
                  {properties?.agent_private_remarks || "No private notes"}
                </p>
              </div>
            )}

            {/* Horizontal Ad Slot */}
            <a href="/store" className="block">
              <div className="w-full h-[150px] bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all cursor-pointer">
                <div className="text-center px-4">
                  <p className="font-semibold text-gray-500 text-sm mb-1">
                    ADVERTISEMENT
                  </p>
                  <p className="text-xs text-gray-400">793px x 150px</p>
                  <p className="text-xs text-gray-400 mt-1">Click to inquire</p>
                </div>
              </div>
            </a>
          </div>

          {/* Features Section */}
          <div className="flex flex-col border-2 p-4">
            <h1 className="text-30 mb-4">Features</h1>
            {Array.isArray(features) && features.length > 0 ? (
              (() => {
                // Group features by category
                const petFeatureIds = [
                  "465",
                  "471",
                  "472",
                  "473",
                  "474",
                  "475",
                  "476",
                ];

                const groupedFeatures = features.reduce((acc, feature) => {
                  // Exclude all pet-related features from the general features badges
                  if (petFeatureIds.includes(String(feature?.feature_id)))
                    return acc;
                  const category = feature?.category || "other";
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(feature);
                  return acc;
                }, {});

                // Format category names for display
                const formatCategory = (cat) => {
                  return cat
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
                };

                // Get sorted categories and split into pages of 12 categories (4 columns × 3 rows)
                const sortedCategories = Object.keys(groupedFeatures).sort();
                const categoriesPerPage = 12;
                const totalPages = Math.ceil(
                  sortedCategories.length / categoriesPerPage,
                );

                const currentCategories = sortedCategories.slice(
                  featurePage * categoriesPerPage,
                  (featurePage + 1) * categoriesPerPage,
                );

                return (
                  <div className="relative">
                    <div
                      className={`grid grid-cols-2 gap-6 auto-rows-min ${
                        sortedCategories.length <= 2
                          ? ""
                          : sortedCategories.length <= 4
                            ? "min-h-[100px]"
                            : sortedCategories.length <= 8
                              ? "min-h-[200px]"
                              : "min-h-[300px]"
                      }`}
                    >
                      {currentCategories.map((category) => (
                        <div
                          key={category}
                          className="border-l-4 border-blue-500 pl-3"
                        >
                          <h3 className="font-semibold text-sm text-gray-700 mb-2">
                            {formatCategory(category)}
                          </h3>
                          <div className="flex flex-wrap gap-1.5">
                            {groupedFeatures[category].map((feature, index) => (
                              <span
                                key={feature?.feature_id || index}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                              >
                                {feature?.feature_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-4 mt-6">
                        <button
                          onClick={() =>
                            setFeaturePage(Math.max(0, featurePage - 1))
                          }
                          disabled={featurePage === 0}
                          className={`px-4 py-2 rounded ${
                            featurePage === 0
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-blue-500 text-white hover:bg-blue-600"
                          }`}
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {featurePage + 1} of {totalPages}
                        </span>
                        <button
                          onClick={() =>
                            setFeaturePage(
                              Math.min(totalPages - 1, featurePage + 1),
                            )
                          }
                          disabled={featurePage === totalPages - 1}
                          className={`px-4 py-2 rounded ${
                            featurePage === totalPages - 1
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-blue-500 text-white hover:bg-blue-600"
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="text-gray-500 text-center py-4">
                <p>No features listed</p>
              </div>
            )}
          </div>

          {/* Pet Policy Section for Residential Lease */}

          {properties?.for === "Lease" &&
            properties?.type === "Residential" &&
            properties?.pets_allowed === "Yes" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h3 className="font-semibold text-lg mb-3 text-blue-800">
                  Pet Policy
                </h3>
                <div className="flex flex-wrap gap-4">
                  {/* Types of Pets */}
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-medium text-gray-700 mb-2">
                      Allowed Pets:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {properties?.pet_dog === "Yes" && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          🐕 Dog
                        </span>
                      )}
                      {properties?.pet_cat === "Yes" && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          🐈 Cat
                        </span>
                      )}
                      {properties?.pet_fish === "Yes" && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          🐠 Fish
                        </span>
                      )}
                      {properties?.pet_bird === "Yes" && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          🐦 Bird
                        </span>
                      )}
                      {properties?.pet_other === "Yes" && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          🐾 Other
                        </span>
                      )}
                      {!properties?.pet_dog &&
                        !properties?.pet_cat &&
                        !properties?.pet_fish &&
                        !properties?.pet_bird &&
                        !properties?.pet_other && (
                          <span className="text-gray-500 italic">
                            Contact for details
                          </span>
                        )}
                    </div>
                  </div>

                  {/* Pet Deposit */}

                  {properties?.pet_deposit && (
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-medium text-gray-700 mb-2">
                        Pet Deposit:
                      </p>

                      <p className="text-lg font-semibold">
                        {currencyFormat(Number(properties.pet_deposit))}

                        {properties?.pet_deposit_nonrefundable === "Yes" && (
                          <span className="text-sm text-red-600 ml-2">
                            (Non-Refundable)
                          </span>
                        )}

                        {properties?.pet_deposit_nonrefundable === "No" && (
                          <span className="text-sm text-green-600 ml-2">
                            (Refundable)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Featured Professionals Section */}
      {promotions && promotions.length > 0 && (
        <div className="mt-6">
          <ProfilePromotionsDisplay promotions={promotions} />
        </div>
      )}

      <div className="flex flex-col border-2 p-4 gap-4">
        <h1 className="font-bold text-30">Comments</h1>
        {/* New comment input */}
        {user ? (
          <div className="flex gap-3">
            <img
              src={
                user?.picture_url
                  ? cloudinaryUrl(user.picture_url)
                  : "/error.png"
              }
              alt="me"
              className="w-12 h-12 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-2">
                Note: Once posted you cannot delete your comment. Each user may
                cast one vote per comment (up or down); changing your vote is
                allowed but you cannot cast multiple simultaneous votes.
              </div>
              <textarea
                maxLength={500}
                value={comment || ""}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write a comment (max 500 chars)..."
                className="w-full border rounded p-2"
                rows={3}
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-sm text-gray-600">
                  {(comment || "").length}/500
                </div>
                <div>
                  <button
                    onClick={async () => {
                      if (!comment || !comment.trim()) return;

                      // Show posting modal
                      setPostingModalOpen(true);
                      setPostingStatus("loading");
                      setPostingMessage("Posting your comment...");

                      const result = await commentCreate(
                        properties?.property_id || validId,
                        comment.trim(),
                        setComments,
                      );

                      if (result.success) {
                        setPostingStatus("success");
                        setPostingMessage("Comment posted successfully!");
                        setComment("");
                        // Auto-close after 2 seconds
                        setTimeout(() => {
                          setPostingModalOpen(false);
                        }, 2000);
                      } else {
                        setPostingStatus("error");
                        setPostingMessage(
                          result.error ||
                            "Failed to post comment. Please try again.",
                        );
                      }
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded"
                  >
                    Post
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full flex justify-center py-4">
            <Link
              to="/login"
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium text-lg shadow-md"
            >
              Log in to Post Comments
            </Link>
          </div>
        )}

        {/* Comments list */}
        <div className="mt-6 space-y-4">
          {Array.isArray(comments) && comments.length > 0 ? (
            (() => {
              // Don't filter out reported comments - show them with "under review" message
              const allComments = comments;
              const regularComments = allComments.filter(
                (c) => !c.comment?.trim().startsWith("@"),
              );
              const replies = allComments.filter((c) =>
                c.comment?.trim().startsWith("@"),
              );

              // Helper to find replies to a username
              const getRepliesForUser = (username) =>
                replies.filter((r) =>
                  r.comment
                    ?.trim()
                    .toLowerCase()
                    .startsWith(`@${username.toLowerCase()}`),
                );

              // Format date/time helper
              const formatDateTime = (dateString) => {
                if (!dateString) return "";
                const date = new Date(dateString);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                });
              };

              // Render a single comment
              const renderComment = (c, isReply = false) => (
                <div
                  key={c.comment_id}
                  className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm ${
                    isReply ? "ml-8 border-l-4 border-l-blue-200" : ""
                  } ${c.is_reported ? "bg-yellow-50 border-yellow-300" : ""}`}
                >
                  {c.is_reported ? (
                    // Show "under review" message for reported comments
                    <div className="flex items-center gap-3 text-yellow-700">
                      <svg
                        className="w-6 h-6 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium">
                          This comment is under review
                        </p>
                        <p className="text-sm text-yellow-600">
                          This comment has been reported and is being reviewed
                          by our moderation team.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      {/* User avatar */}
                      <img
                        src={
                          c.picture_url
                            ? cloudinaryUrl(c.picture_url)
                            : "/error.png"
                        }
                        alt={c.username}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />

                      <div className="flex-1 min-w-0">
                        {/* Header row: name, username, and timestamp */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-sm">
                            {c.first_name && c.last_name
                              ? `${c.first_name} ${c.last_name}`
                              : c.username}
                          </div>
                          <div className="text-xs text-gray-500">
                            @{c.username}
                          </div>
                          <div className="text-xs text-gray-400">
                            • {formatDateTime(c.created_at)}
                          </div>
                        </div>

                        {/* Comment content */}
                        <div className="mt-2 text-sm">{c.comment}</div>

                        {/* Action buttons row: Vote, Reply, Delete, Report */}
                        <div className="flex items-center gap-4 mt-3">
                          {/* Vote buttons */}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                if (!user) return setShowLoginModal(true);
                                commentReact(
                                  c.comment_id,
                                  properties?.property_id || validId,
                                  1,
                                  setComments,
                                );
                              }}
                              className="p-1.5 rounded-full hover:bg-blue-50 transition-colors group"
                              title="Upvote"
                            >
                              <svg
                                className="w-5 h-5 stroke-gray-500 group-hover:stroke-blue-600 transition-colors"
                                fill="none"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 15l7-7 7 7"
                                />
                              </svg>
                            </button>
                            <span className="text-sm font-medium min-w-[20px] text-center">
                              {c.likes || 0}
                            </span>
                            <button
                              onClick={() => {
                                if (!user) return setShowLoginModal(true);
                                commentReact(
                                  c.comment_id,
                                  properties?.property_id || validId,
                                  -1,
                                  setComments,
                                );
                              }}
                              className="p-1.5 rounded-full hover:bg-blue-50 transition-colors group"
                              title="Downvote"
                            >
                              <svg
                                className="w-5 h-5 stroke-gray-500 group-hover:stroke-blue-600 transition-colors"
                                fill="none"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>

                          {/* Reply button */}
                          <button
                            onClick={() => {
                              if (!user) return setShowLoginModal(true);
                              // Pre-fill the comment box with @mention
                              setComment(`@${c.username} `);
                              // Scroll to comment input
                              document
                                .querySelector(
                                  'textarea[placeholder*="Write a comment"]',
                                )
                                ?.focus();
                            }}
                            className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 transition-colors"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                              />
                            </svg>
                            Reply
                          </button>

                          {/* Admin Delete button */}
                          {isAdmin && (
                            <button
                              onClick={() => {
                                setAdminDeleteCommentId(c.comment_id);
                                setAdminDeleteReason("");
                                setAdminDeleteModalOpen(true);
                              }}
                              className="flex items-center gap-1 text-sm text-orange-500 hover:text-orange-700 transition-colors"
                              title="Admin: Delete this comment"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              Delete
                            </button>
                          )}

                          {/* Report button */}
                          <button
                            onClick={() => {
                              if (!user) return setShowLoginModal(true);
                              setReportingCommentId(c.comment_id);
                              setReportModalOpen(true);
                            }}
                            className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 transition-colors ml-auto"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                              />
                            </svg>
                            Report
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );

              // Render comments with nested replies
              return (
                <>
                  {regularComments.map((c) => (
                    <div key={c.comment_id}>
                      {renderComment(c, false)}
                      {/* Render replies to this user */}
                      {getRepliesForUser(c.username).map((reply) =>
                        renderComment(reply, true),
                      )}
                    </div>
                  ))}
                  {/* Render orphan replies (replies to users not in this thread) */}
                  {replies
                    .filter(
                      (r) =>
                        !regularComments.some((c) =>
                          r.comment
                            ?.trim()
                            .toLowerCase()
                            .startsWith(`@${c.username.toLowerCase()}`),
                        ),
                    )
                    .map((r) => renderComment(r, true))}
                </>
              );
            })()
          ) : (
            <div className="text-center text-sm text-gray-600 py-8">
              No comments yet. Be the first to comment.
            </div>
          )}
        </div>
      </div>

      {reportModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-2xl">
            {!reportSubmitted ? (
              <>
                <h2 className="font-bold mb-2">Report Comment</h2>
                <p className="text-sm text-gray-600 mb-2">
                  Why are you reporting this comment?
                </p>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full border p-2 rounded mb-2"
                >
                  <option value="">Select a reason...</option>
                  <option value="Spam or advertising">
                    Spam or advertising
                  </option>
                  <option value="Harassment or hate speech">
                    Harassment or hate speech
                  </option>
                  <option value="False or misleading information">
                    False or misleading information
                  </option>
                  <option value="Scam or fraud">Scam or fraud</option>
                  <option value="Inappropriate content">
                    Inappropriate content
                  </option>
                  <option value="Off-topic">Off-topic</option>
                  <option value="Other">Other</option>
                </select>
                <textarea
                  maxLength={1000}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  className="w-full border p-2 rounded mb-2"
                  placeholder="Additional details (optional)"
                  rows={4}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setReportModalOpen(false);
                      setReportSubmitted(false);
                    }}
                    className="px-3 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!reportReason || reportReason.trim().length === 0)
                        return;
                      const resp = await commentReport(
                        reportingCommentId,
                        user?.user_id || null,
                        reportReason.trim(),
                        reportDetails || null,
                      );

                      // Check if already reported
                      if (resp && resp.already_reported === 1) {
                        setAlert([
                          "Already Reported",
                          "This comment has already been reported and is currently under review.",
                          "confirm",
                        ]);
                        setReportModalOpen(false);
                        setReportReason("");
                        setReportDetails("");
                        setReportingCommentId(null);
                        return;
                      }

                      setReportSubmitted(true);
                      // Refresh comments to show updated status
                      if (properties?.property_id || validId) {
                        commentGet(
                          properties?.property_id || validId,
                          setComments,
                        );
                      }
                    }}
                    className="px-3 py-2 bg-red-600 text-white rounded"
                  >
                    Send Report
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full bg-yellow-100 p-3">
                  <svg
                    className="w-12 h-12"
                    viewBox="0 0 24 24"
                    fill="#FCD34D"
                    stroke="#000000"
                    strokeWidth="1.5"
                  >
                    <path d="M12 2L2 20h20L12 2z" />
                    <text
                      x="12"
                      y="16"
                      textAnchor="middle"
                      fontSize="12"
                      fontWeight="bold"
                      fill="#000000"
                    >
                      !
                    </text>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-center">
                  Comment Reported
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  This comment has been reported and is now under review. Thank
                  you for helping keep our community safe.
                </p>
                <button
                  onClick={() => {
                    setReportModalOpen(false);
                    setReportSubmitted(false);
                    setReportReason("");
                    setReportDetails("");
                    setReportingCommentId(null);
                  }}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Login-required modal for comment interactions */}
      {showLoginModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-2xl">
            <h2 className="font-bold mb-2">Please log in</h2>
            <p className="text-sm text-gray-600 mb-4">
              You must be logged in to upvote, downvote, reply, or report
              comments.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowLoginModal(false)}
                className="px-3 py-2 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  nav("/login");
                }}
                className="px-3 py-2 bg-blue-600 text-white rounded"
              >
                Log in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Delete Modal */}
      {adminDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-2xl">
            <h2 className="font-bold mb-2 text-orange-600">
              Admin: Delete Comment
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for deleting this comment. This action
              cannot be undone.
            </p>
            <textarea
              maxLength={500}
              value={adminDeleteReason}
              onChange={(e) => setAdminDeleteReason(e.target.value)}
              className="w-full border border-gray-300 p-3 rounded-lg mb-4 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Reason for deletion (required)..."
              rows={4}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setAdminDeleteModalOpen(false);
                  setAdminDeleteCommentId(null);
                  setAdminDeleteReason("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={adminDeleteSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!adminDeleteReason.trim()) {
                    alert("Please provide a reason for deletion");
                    return;
                  }
                  setAdminDeleteSubmitting(true);
                  const result = await commentDeleteAdmin(adminDeleteCommentId);
                  setAdminDeleteSubmitting(false);

                  if (result?.success !== false) {
                    // Refresh comments
                    if (properties?.property_id || validId) {
                      commentGet(
                        properties?.property_id || validId,
                        setComments,
                      );
                    }
                    setAdminDeleteModalOpen(false);
                    setAdminDeleteCommentId(null);
                    setAdminDeleteReason("");
                    setAlert([
                      "Comment Deleted",
                      `Comment has been deleted. Reason: ${adminDeleteReason.trim()}`,
                      "confirm",
                    ]);
                  } else {
                    alert(result?.error || "Failed to delete comment");
                  }
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                disabled={adminDeleteSubmitting || !adminDeleteReason.trim()}
              >
                {adminDeleteSubmitting ? "Deleting..." : "Delete Comment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Posting modal */}
      {postingModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-2xl">
            {postingStatus === "loading" && (
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="text-lg font-medium">{postingMessage}</p>
              </div>
            )}
            {postingStatus === "success" && (
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full bg-green-100 p-3">
                  <svg
                    className="w-12 h-12 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium text-green-600">
                  {postingMessage}
                </p>
              </div>
            )}
            {postingStatus === "error" && (
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full bg-red-100 p-3">
                  <svg
                    className="w-12 h-12 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium text-red-600">
                  {postingMessage}
                </p>
                <button
                  onClick={() => setPostingModalOpen(false)}
                  className="mt-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Showing Request Modal */}
      {showingModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  📅 Schedule a Showing
                </h2>
                <button
                  onClick={() => setShowingModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              {showingSuccess ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">✅</div>
                  <h3 className="text-xl font-bold text-green-600 mb-2">
                    Request Sent Successfully!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Your showing request has been sent to the listing agent.
                    They will contact you soon.
                  </p>
                  <button
                    onClick={() => setShowingModalOpen(false)}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  {/* Property Info */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <p className="font-semibold text-gray-900">
                      {properties?.street_number} {properties?.street_name}
                    </p>
                    <p className="text-gray-600 text-sm">
                      {properties?.city}, TX {properties?.zip}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      Listing Agent: {getDisplayName(poster)}
                    </p>
                  </div>

                  {/* Date Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={showingDate}
                      onChange={(e) => setShowingDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  {/* Time Selection - Preset Time Slots */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Time <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Select your availability window
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        {
                          label: "Morning",
                          sublabel: "8AM - 11AM",
                          start: "08:00",
                          end: "11:00",
                        },
                        {
                          label: "Midday",
                          sublabel: "11AM - 1PM",
                          start: "11:00",
                          end: "13:00",
                        },
                        {
                          label: "Afternoon",
                          sublabel: "1PM - 4PM",
                          start: "13:00",
                          end: "16:00",
                        },
                        {
                          label: "Late Afternoon",
                          sublabel: "4PM - 6PM",
                          start: "16:00",
                          end: "18:00",
                        },
                        {
                          label: "Evening",
                          sublabel: "6PM - 8PM",
                          start: "18:00",
                          end: "20:00",
                        },
                        {
                          label: "Flexible",
                          sublabel: "Any time",
                          start: "flexible",
                          end: "flexible",
                        },
                      ].map((slot) => (
                        <button
                          key={slot.label}
                          type="button"
                          onClick={() => {
                            setShowingTimeStart(slot.start);
                            setShowingTimeEnd(slot.end);
                          }}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            showingTimeStart === slot.start &&
                            showingTimeEnd === slot.end
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-gray-200 hover:border-green-300 hover:bg-green-50"
                          }`}
                        >
                          <span className="block font-semibold text-sm">
                            {slot.label}
                          </span>
                          <span className="block text-xs text-gray-500">
                            {slot.sublabel}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Additional Message */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Message (Optional)
                    </label>
                    <textarea
                      value={showingMessage}
                      onChange={(e) => setShowingMessage(e.target.value)}
                      placeholder="Any special requests or questions..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Your Contact Info */}
                  {user && (
                    <div className="bg-blue-50 rounded-lg p-4 mb-6">
                      <p className="text-sm font-semibold text-blue-800 mb-1">
                        Your Contact Information
                      </p>
                      <p className="text-sm text-blue-700">
                        {getDisplayName(user)}
                      </p>
                      {user.email_address && (
                        <p className="text-sm text-blue-700">
                          {user.email_address}
                        </p>
                      )}
                      {user.phone_number && (
                        <p className="text-sm text-blue-700">
                          {user.phone_number}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowingModalOpen(false)}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-semibold transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleShowingSubmit}
                      disabled={showingSubmitting}
                      className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-3 rounded-lg font-semibold transition-all"
                    >
                      {showingSubmitting
                        ? "Sending..."
                        : "Submit Showing Request"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
      {modal && properties && (
        <ModalEditProperty
          modal={modal}
          setModal={setModal}
          property={properties}
          setProperty={setProperties}
        />
      )}
    </div>
  );
};

export default PropertyView;
